/**
 * UnknownContactsPanel — unified panel for unknown contacts from any sync channel.
 *
 * items: [{ email, name?, firm_guess?, meta?, source?, prefill? }]
 *   meta     — optional string shown in the subtitle row (e.g. "↩ replied · 3d ago")
 *   prefill  — extra fields forwarded to contact form on Add
 *
 * onAdd:     (prefill) => void   — open contact form with prefill
 * onIgnore:  (email)   => void   — dismiss locally (pass null to hide Ignore button)
 * onHandled: (email, contact?) => void — called after match OR add; triggers cross-channel rescan
 */

import { useState } from 'react';
import MatchContactPanel from './MatchContactPanel.jsx';

const COLLAPSE_THRESHOLD = 4;

function normalizeEmail(v) {
  return String(v ?? '').trim();
}

export default function UnknownContactsPanel({
  title,
  icon        = '⚠',
  bgColor     = '#fffbeb',
  borderColor = '#fde68a',
  titleColor  = '#92400e',
  items       = [],
  getToken,
  firms       = [],
  onAdd,
  onIgnore,
  onHandled,
}) {
  const [expanded,  setExpanded]  = useState(items.length <= COLLAPSE_THRESHOLD);
  const [matchItem, setMatchItem] = useState(null);
  const [linkedMap, setLinkedMap] = useState({}); // email.lower → contact

  if (!items.length) return null;

  const collapsible = items.length > COLLAPSE_THRESHOLD;
  const pending = items.filter((r) => !linkedMap[normalizeEmail(r.email).toLowerCase()]);
  const matched = items.filter((r) =>  linkedMap[normalizeEmail(r.email).toLowerCase()]);

  const handleLinked = (email, contact) => {
    setLinkedMap((prev) => ({ ...prev, [email.toLowerCase()]: contact }));
    setMatchItem(null);
    onHandled?.(email, contact);
  };

  const handleAdd = (item) => {
    const email = normalizeEmail(item.email);
    const prefill = {
      email,
      name:      item.name ?? '',
      firm_name: item.firm_guess ?? '',
      source:    item.source ?? title,
      ...(item.prefill ?? {}),
    };
    onAdd?.(prefill);
    onHandled?.(email);
  };

  return (
    <>
      {matchItem && (
        <MatchContactPanel
          item={matchItem}
          firms={firms}
          getToken={getToken}
          onClose={() => setMatchItem(null)}
          onLinked={handleLinked}
          onAddNew={(prefill) => {
            setMatchItem(null);
            onAdd?.(prefill);
          }}
        />
      )}

      <div style={{
        background: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: 10,
        padding: '14px 16px',
        marginBottom: 18,
      }}>
        {/* Header row */}
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            marginBottom: expanded ? 12 : 0,
            cursor: collapsible ? 'pointer' : 'default',
            userSelect: 'none',
          }}
          onClick={collapsible ? () => setExpanded((v) => !v) : undefined}
        >
          <span style={{ fontSize: 16 }}>{icon}</span>
          <span style={{ fontWeight: 700, fontSize: 14, color: titleColor }}>
            {pending.length > 0
              ? `${pending.length} unknown contact${pending.length > 1 ? 's' : ''} from ${title}`
              : `All contacts from ${title} matched ✓`}
          </span>
          {!expanded && pending.length > 0 && (
            <span style={{
              fontSize: 11.5, fontWeight: 700, color: titleColor,
              background: borderColor, borderRadius: 99, padding: '1px 8px',
            }}>needs review</span>
          )}
          <span style={{ fontSize: 12.5, color: 'var(--c-text-2)', marginLeft: 'auto' }}>
            {collapsible ? (expanded ? 'collapse ▴' : 'click to expand ▾') : ''}
          </span>
        </div>

        {expanded && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Pending rows */}
            {pending.map((item) => (
              <div key={normalizeEmail(item.email) || item.name || Math.random()} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: '#fff', border: '1px solid var(--c-border)',
                borderRadius: 8, padding: '9px 12px',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.name || normalizeEmail(item.email) || '(unknown contact)'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--c-text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {normalizeEmail(item.email) && <span>{normalizeEmail(item.email)}</span>}
                    {item.firm_guess && <span>{normalizeEmail(item.email) ? ' · ' : ''}{item.firm_guess}</span>}
                    {item.meta && <span style={{ color: 'var(--c-text-3)' }}>{normalizeEmail(item.email) || item.firm_guess ? ' · ' : ''}{item.meta}</span>}
                  </div>
                  {item.match_reason === 'name_institution_match' && (
                    <div style={{ fontSize: 11.5, color: '#92400e', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 4, padding: '2px 7px', marginTop: 5, display: 'inline-block' }}>
                      ⚠ Possible duplicate — someone with a similar name already exists at this firm. Check Contacts before adding.
                    </div>
                  )}
                </div>
                <button className="btn btn--sm btn--primary" onClick={() => setMatchItem(item)}>Match / Add</button>
                {onIgnore && (
                  <button className="btn btn--sm btn--ghost" onClick={() => onIgnore(normalizeEmail(item.email))}>Dismiss</button>
                )}
              </div>
            ))}

            {/* Matched rows */}
            {matched.map((item) => {
              const email = normalizeEmail(item.email);
              const contact = linkedMap[email.toLowerCase()];
              return (
                <div key={email || item.name || Math.random()} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: '#f0fdf4', border: '1px solid #bbf7d0',
                  borderRadius: 8, padding: '9px 12px',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 600, fontSize: 13.5 }}>{contact?.name || email || '(unknown contact)'}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#15803d', background: '#dcfce7', borderRadius: 99, padding: '1px 6px' }}>matched ✓</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--c-text-2)' }}>{email || 'no email on record'}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
